import React, { useEffect } from 'react';
import { ArrowLeft, Clock, Users, Shield, Activity, TrendingUp, AlertCircle, CheckCircle, Zap, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import TableOfContents from '../components/TableOfContents';

const MedhaScribeCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'problem', label: 'The Problem' },
    { id: 'constraints', label: 'Constraints' },
    { id: 'opportunity', label: 'Opportunity' },
    { id: 'role', label: 'My Role' },
    { id: 'research', label: 'Research' },
    { id: 'strategy', label: 'Design Strategy' },
    { id: 'solution', label: 'Solution' },
    { id: 'measurement', label: 'Measurement' },
    { id: 'results', label: 'Results' },
    { id: 'next', label: 'Next Steps' }
  ];

  return (
    <div className="bg-pastel-bg min-h-screen font-sans text-neutral-900 selection:bg-accent/20 relative">
      
      <TableOfContents sections={sections} />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-pastel-bg/90 backdrop-blur-md">
        <div className="container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-neutral-600 hover:text-accent transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="h-px bg-neutral-200/60 mx-6 md:mx-12" />
      </nav>

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          
          {/* 1. Header */}
          <header id="overview" className="mb-20 scroll-mt-32">
            <h1 className="text-4xl md:text-6xl font-serif text-neutral-900 mb-6 leading-tight">
              Medha Scribe - Ambient Clinical documentation tool
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed max-w-3xl mb-6">
              AI-powered medical scribe designed for clinicians across mobile and enterprise hospital systems.
            </p>

            <a 
              href="https://www.medha-analytics.ai/medhascribe" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-accent font-medium mb-10 hover:underline"
            >
              View Live Product <ArrowUpRight className="w-4 h-4" />
            </a>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Win</h3>
                <p className="text-lg font-bold">50% time cut</p>
                <p className="text-sm text-neutral-500">for documentation</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Scale</h3>
                <p className="text-lg font-bold">20+ Hospitals</p>
                <p className="text-sm text-neutral-500">multiple specialties</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">My Role</h3>
                <p className="text-lg font-bold">Lead UX Designer</p>
                <p className="text-sm text-neutral-500">Research, Strategy, UI/UX</p>
              </div>
            </div>
          </header>

          {/* 2. The Problem */}
          <section id="problem" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">The Problem</h2>
            <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
              Clinicians were losing critical patient-care time to documentation. Existing voice tools were expensive, inaccurate with medical terminology, and poorly integrated into hospital workflows—making real-world adoption difficult in live clinical environments.
            </p>
            <p className="text-lg text-neutral-600 mb-12 leading-relaxed">
              As a result, clinicians continued to rely on manual, repetitive documentation despite high cognitive and time costs.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-accent" /> Clinical Impact
                </h3>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li>• Documentation reduced face-to-face patient time</li>
                  <li>• Repetitive formatting increased fatigue</li>
                  <li>• Errors directly affected care quality</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-accent" /> Operational Impact
                </h3>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li>• High time and cost per report</li>
                  <li>• Multiple handoffs and verification loops</li>
                  <li>• Dependency on manual steps reduced throughput</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-accent" /> Emotional Impact
                </h3>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li>• Cognitive overload and decision fatigue</li>
                  <li>• Mental strain from balancing accuracy & speed</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Constraints */}
          <section id="constraints" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Constraints & Reality Check</h2>
            <div className="flex flex-wrap gap-3">
              {[
                "Zero tolerance for medical/legal errors",
                "No 'trying' in live environments",
                "No disruption to Medha Scribe workflows",
                "High privacy sensitivity",
                "Anxiety around mic control"
              ].map((tag, i) => (
                <span key={i} className="bg-white border border-neutral-200 px-5 py-3 rounded-full text-sm font-medium text-neutral-700 shadow-sm">
                  {tag}
                </span>
              ))}
            </div>
            <p className="mt-6 text-neutral-500 italic text-sm">
              These constraints shaped every design decision and ruled out experimental or intrusive interaction models.
            </p>
          </section>

          {/* 4. Opportunity Statement */}
          <section id="opportunity" className="mb-24 bg-accent/5 p-10 rounded-[2rem] border border-accent/10 scroll-mt-32">
            <h2 className="text-xl font-bold text-accent mb-4">Opportunity Statement</h2>
            <p className="text-2xl font-serif text-neutral-900 leading-snug mb-6">
              If we could design a voice-first documentation system that worked within clinicians’ existing workflows—rather than requiring behavior change—we could significantly reduce documentation time without increasing cognitive load or adoption risk.
            </p>
            <p className="text-neutral-600">
              The strategy was to start with small, high-confidence use cases, prove reliability and trust, and then expand gradually into more complex clinical scenarios.
            </p>
          </section>

          {/* 5. Role */}
          <section id="role" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">My Role & Ownership</h2>
            <div className="bg-white p-8 rounded-3xl border border-neutral-100">
              <ul className="space-y-4">
                {[
                  "Defining the UX strategy for voice-first documentation",
                  "Leading generative and evaluative research",
                  "Overseeing designs & collaborating with engineering",
                  "Defining success metrics and adoption signals (post-MVP)"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-lg text-neutral-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 6. Research */}
          <section id="research" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-12">Research → Insight → Decision</h2>
            
            <div className="mb-12">
              <h3 className="text-xl font-bold mb-4">JTBD & Task Analysis</h3>
              
              <div className="mb-8 rounded-2xl overflow-hidden shadow-sm border border-neutral-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/xqi33ua5_Screenshot%202026-01-08%20at%204.25.27%E2%80%AFPM.png" 
                  alt="JTBD and Task Analysis Diagram" 
                  className="w-full h-auto"
                />
              </div>

              <div className="bg-neutral-900 text-white p-8 rounded-2xl shadow-lg">
                <p className="text-lg italic leading-relaxed">
                  "When I’m scanning or consulting, I need to capture findings instantly without breaking focus, so I can complete reports accurately and move to the next patient on time."
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4">Shadow Visits & Observations</h3>
              <div className="mb-4 rounded-3xl overflow-hidden shadow-sm border border-neutral-200">
                <img 
                  src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/vgoii8py_Screenshot%202026-01-09%20at%207.01.56%E2%80%AFAM.png" 
                  alt="Shadow Visit Observations" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-neutral-500 text-sm">Shadowing clinicians revealed insights that contradicted early assumptions.</p>
            </div>
          </section>

          {/* 7. Design Strategy */}
          <section id="strategy" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">From Insight → Design Strategy</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { title: "Dynamic", desc: "Learns and improves based on user inputs over time" },
                { title: "Adaptive", desc: "Responds to different clinical contexts and workflows" },
                { title: "Portable", desc: "Usable across locations and devices" },
                { title: "Fast to Navigate", desc: "Supports high-pressure, fast-moving environments" }
              ].map((card, i) => (
                <div key={i} className="aspect-square bg-pastel-cream p-8 rounded-[2rem] flex flex-col justify-center text-center hover:shadow-md transition-shadow border border-neutral-100">
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">{card.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 8. Solution */}
          <section id="solution" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Solution</h2>
            <div className="bg-white rounded-3xl h-96 flex items-center justify-center border border-neutral-200 shadow-sm mb-8 relative overflow-hidden">
               <div className="absolute inset-0 bg-neutral-50 flex items-center justify-center">
                  <span className="text-neutral-400 font-medium">[Screen can be shared in presentation]</span>
               </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <p className="text-neutral-600 leading-relaxed">
                Rather than fully automating report generation early on, we intentionally allowed manual review and correction to maintain clinician trust and confidence in accuracy.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                Automation was introduced progressively, aligned with usage patterns and comfort levels.
              </p>
            </div>
          </section>

          {/* 9. Measurement */}
          <section id="measurement" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Measurement & Validation</h2>
            <div className="bg-white p-8 rounded-3xl border border-neutral-100">
              <p className="text-lg text-neutral-700 mb-6">
                To validate real-world adoption and effectiveness, we implemented analytics and qualitative feedback loops.
              </p>
              <div className="flex items-center gap-4">
                <TrendingUp className="w-6 h-6 text-accent" />
                <span className="text-lg font-bold">Success Metric: Documentation time saved</span>
              </div>
            </div>
          </section>

          {/* 10. Results */}
          <section id="results" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Results & Learnings</h2>
            
            <div className="bg-neutral-900 text-white p-10 rounded-[2.5rem] mb-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <h3 className="text-4xl font-bold text-accent mb-2">20+</h3>
                  <p className="text-neutral-400">Hospitals Rolled Out</p>
                </div>
                <div>
                  <h3 className="text-4xl font-bold text-accent mb-2">50%+</h3>
                  <p className="text-neutral-400">Time Reduction</p>
                </div>
                <div>
                  <h3 className="text-4xl font-bold text-accent mb-2">High</h3>
                  <p className="text-neutral-400">Clinician Throughput</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4">Post-MVP Learnings</h3>
              <ul className="space-y-3 text-neutral-600">
                <li className="bg-orange-50 p-4 rounded-xl border border-orange-100">Navigation complexity surfaced as workflows expanded</li>
                <li className="bg-orange-50 p-4 rounded-xl border border-orange-100">High navigation frequency indicated underlying workflow fragmentation</li>
                <li className="bg-green-50 p-4 rounded-xl border border-green-100">Subsequent iterations focused on simplifying navigation and reducing context switching</li>
              </ul>
            </div>
          </section>

          {/* 11. Next */}
          <section id="next" className="mb-20 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">What is Improving Next</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-8 bg-red-50 rounded-3xl border border-red-100">
                <h3 className="font-bold text-red-800 mb-4">What didn't work well</h3>
                <ul className="space-y-2 text-red-700">
                  <li>• Navigation across multiple systems (Atma + Medha Scribe) introduced friction</li>
                  <li>• Context switching between background systems increased cognitive load</li>
                </ul>
              </div>
              <div className="p-8 bg-blue-50 rounded-3xl border border-blue-100">
                <h3 className="font-bold text-blue-800 mb-4">Next Improvements</h3>
                <p className="text-blue-700">
                  Earlier investment in navigation scalability as use cases expand.
                </p>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default MedhaScribeCaseStudy;
